import { useRef, useState } from 'react';
import { useProspectingAvailability } from './useProspectingAvailability';
import { copyMessageLabel, copyPickerGroups, ordinaryCopyChoices } from './copyMessages';
import { Button, Select } from '../../components/ui';
import { EmailBodyEditor } from './EmailBodyEditor';
import { usePhotoPicker } from './usePhotoPicker';
import { useAuth } from '../auth/AuthProvider';
import { photosInBody, resolvePhotos } from '../gmail/emailPhotos';
import { displayName, replySubject, sizeLabel } from '../gmail/mailThread';
import type { MailThread } from '../gmail/mailThread';
import { MAX_ATTACHMENTS, MAX_ATTACHMENT_BYTES, useSendAsMailbox } from '../gmail/useMailbox';
import { useCopySets } from './hooks';
import { copyEmail, copySetTouches, replaceTokens } from './types';
import type { Lead, Touch } from './types';

/**
 * The reply box under a conversation.
 *
 * HubSpot's one layout idea worth copying (docs/sales-inbox-design-note.md
 * and the 3 September note): you never leave the conversation to answer it.
 * The box sits under the messages, the subject is the thread's, and what goes
 * out is a reply in that thread — In-Reply-To and References set from the
 * message being answered, which the function reads from Gmail itself.
 *
 * Kyu: attachments are "mostly in the next few emails". So this is where
 * files are: a follow-up in a live conversation, not a first touch.
 *
 * Only drawn for the three senders (the caller checks); everyone else reads.
 */

const replySubjectOf = (thread: MailThread) => replySubject(thread.latest.subject);

export function LeadMailReply({
  lead,
  thread,
  onSent,
  onCancel,
}: {
  lead: Lead;
  thread: MailThread;
  /** The reply went out — refetch the conversation so it appears. */
  onSent: () => void;
  onCancel: () => void;
}) {
  const copySetsQuery = useCopySets();
  const { message_enabled } = useProspectingAvailability();
  const send = useSendAsMailbox();
  const { client } = useAuth();
  const photoPicker = usePhotoPicker();
  const fileInput = useRef<HTMLInputElement>(null);
  const [copySetId, setCopySetId] = useState('');
  const [touch, setTouch] = useState<Touch>(2);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState('');

  const sets = copySetsQuery.data ?? [];
  const set = sets.find((c) => c.id === copySetId) ?? null;
  const subject = replySubjectOf(thread);
  // Photos in the body count with the files: one email, one cap. Only the
  // ones the body still names — a photo picked and then deleted travels
  // nowhere, so it must not go on eating the budget until a page refresh.
  const carried = photosInBody(body, photoPicker.photos);
  const totalBytes =
    files.reduce((n, f) => n + f.size, 0) + carried.reduce((n, f) => n + f.size, 0);
  const tooMany = files.length + carried.length > MAX_ATTACHMENTS;
  const tooBig = totalBytes > MAX_ATTACHMENT_BYTES;

  // A copy set fills the body once, when chosen. Typing afterwards is the
  // person's; the template is a starting point, not a cage (LeadPanel, drop 81).
  function prefill(nextSetId: string, nextTouch: Touch) {
    const chosen = sets.find((c) => c.id === nextSetId) ?? null;
    if (chosen === null) return;
    setBody(replaceTokens(copyEmail(chosen, nextTouch).body, lead));
  }

  function addFiles(list: FileList | null) {
    if (list === null) return;
    setFiles([...files, ...Array.from(list)]);
    if (fileInput.current) fileInput.current.value = '';
  }

  function sendNow() {
    if (body.trim() === '') {
      setMessage('Write the reply first, or pick a copy set.');
      return;
    }
    if (tooMany || tooBig) {
      setMessage(
        tooMany
          ? `No more than ${MAX_ATTACHMENTS} attachments on one email.`
          : 'The attachments add up to more than 20 MB. Send fewer, or smaller, files.',
      );
      return;
    }
    setMessage('');
    void resolvePhotos(body, photoPicker.photos, client)
      .then((resolved) => {
        send.mutate(
          {
            leadId: lead.id,
            subject,
            body: resolved.body,
            note:
              set === null
                ? 'Reply · written by hand'
                : `Reply · Email ${String(touch)} · ${message_enabled ? copyMessageLabel(set) : set.name}`,
            replyTo: thread.latest.id,
            ...(files.length > 0 ? { attachments: files } : {}),
            ...(resolved.inlinePhotos.length > 0 ? { inlinePhotos: resolved.inlinePhotos } : {}),
          },
          {
            onSuccess: (result) => {
              const record =
                result.outcome === 'ticked'
                  ? `Ticked Email ${String(touch)} on the list.`
                  : result.outcome === 'logged'
                    ? 'Logged on the timeline.'
                    : result.outcome === 'already'
                      ? 'Already on the timeline.'
                      : 'Sent, but the timeline entry failed — tell Kyu.';
              const carried =
                result.attachments === 0
                  ? ''
                  : ` ${String(result.attachments)} file${result.attachments === 1 ? '' : 's'} attached.`;
              setMessage(`Sent in this conversation.${carried} ${record}`);
              setBody('');
              setFiles([]);
              photoPicker.reset();
              setCopySetId('');
              onSent();
            },
            onError: (cause) => setMessage(cause.message),
          },
        );
      })
      .catch((cause: unknown) => {
        setMessage(cause instanceof Error ? cause.message : 'The photos could not be read.');
      });
  }

  return (
    <div className="lead-mail-reply" aria-label="Reply">
      <p className="lead-mail-reply-head">
        <span className="lead-mail-reply-to">Reply to {displayName(thread.latest.from)}</span>
        <span className="lead-mail-reply-subject">{subject}</span>
      </p>

      {sets.length === 0 ? null : (
        <div className="lead-mail-reply-sets">
          <Select
            label="Start from a copy set"
            value={copySetId}
            options={[
              { value: '', label: 'Write from scratch' },
              ...(message_enabled ? ordinaryCopyChoices(sets) : sets).map((c) => ({
                value: c.id,
                label: message_enabled ? copyMessageLabel(c) : c.name,
              })),
            ]}
            groups={message_enabled ? copyPickerGroups(sets, true) : []}
            onChange={(e) => {
              setCopySetId(e.target.value);
              prefill(e.target.value, touch);
            }}
          />
          {set === null ? null : (
            <Select
              label="Touch"
              value={String(touch)}
              options={copySetTouches(set).map((v) => ({
                value: String(v),
                label: `Email ${v}`,
              }))}
              onChange={(e) => {
                const next = Number(e.target.value) as Touch;
                setTouch(next);
                prefill(copySetId, next);
              }}
            />
          )}
        </div>
      )}

      <EmailBodyEditor
        label="Reply"
        labelHidden
        value={body}
        rows={8}
        placeholder="Write the reply…"
        onChange={setBody}
        onPhoto={photoPicker.pick}
        photoPreview={photoPicker.previewFor}
      />
      {photoPicker.PhotoInput}

      {files.length === 0 ? null : (
        <ul className="lead-mail-attachments" aria-label="Files to send">
          {files.map((file, index) => (
            <li key={`${file.name}-${String(index)}`} className="lead-mail-attachment">
              <span className="lead-mail-attachment-name">{file.name}</span>
              <span className="lead-mail-attachment-size">{sizeLabel(file.size)}</span>
              <button
                type="button"
                className="lead-mail-attachment-remove"
                aria-label={`Remove ${file.name}`}
                onClick={() => setFiles(files.filter((_, i) => i !== index))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {!(tooMany || tooBig) ? null : (
        <p className="lead-mail-reply-warning">
          {tooMany
            ? `No more than ${MAX_ATTACHMENTS} attachments on one email.`
            : `That is ${sizeLabel(totalBytes)} of files; the most one email can carry is 20 MB.`}
        </p>
      )}

      <p className="tracker-field-note">
        Goes out from Alex&apos;s mailbox, in this conversation, with his Gmail signature. Recorded
        on this lead as sent by you.
      </p>

      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        aria-label="Attach files"
        onChange={(e) => addFiles(e.target.files)}
      />
      <div className="lead-send-actions">
        <Button size="sm" onClick={sendNow} disabled={send.isPending}>
          {send.isPending ? 'Sending…' : 'Send as Alex'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={send.isPending}
          onClick={() => fileInput.current?.click()}
        >
          Attach files
        </Button>
        <Button variant="ghost" size="sm" disabled={send.isPending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {message === '' ? null : <p className="lead-mail-reply-message">{message}</p>}
    </div>
  );
}
